import { LegacyRef, memo, ReactNode, useCallback, useEffect, useState } from "react";
import classNames from "classnames";

import styles from "./style.module.scss";


export type PageLayoutProps = {
  children?: ReactNode,
  header?: ReactNode,
  className?: string,
  headerClassName?: string,
  contentClassName?: string,
}

export const PageLayout = memo<PageLayoutProps>(
  (
    {
      children,
      header,
      className,
      headerClassName,
      contentClassName,
    }) => {
    const [ isSticky, setSticky ] = useState(false);
    const [ currentNode, setCurrentNode ] = useState<HTMLDivElement | null>(null);
    const ref = useCallback((ref: HTMLDivElement | null) => setCurrentNode(ref), []) as LegacyRef<HTMLDivElement>;

    useEffect(() => {
      if (currentNode != null) {
        const animation = {
          frame: 0,
        };

        let node = currentNode as HTMLElement;
        const parentNodes = [] as HTMLElement[];
        while (node?.parentElement != null) {
          parentNodes.push(node.parentElement);
          node = node?.parentElement;
        }

        const handleScroll = (ev: Event) => {
          cancelAnimationFrame(animation.frame);
          animation.frame = requestAnimationFrame(time => {
            if (currentNode.getBoundingClientRect()) {
              setSticky((ev.target as HTMLElement).scrollTop > 0);
            }
          });
        };

        parentNodes.forEach(parent => parent?.addEventListener("scroll", handleScroll));
        return () => {
          cancelAnimationFrame(animation.frame);
          parentNodes.forEach(parent => parent?.removeEventListener("scroll", handleScroll));
        };
      }
    }, [ currentNode ]);

    return (
      <div className={classNames(styles.root, className)} ref={ref}>
        {
          header &&
          <div
            data-page-header-sticky={isSticky}
            className={classNames(styles.header, headerClassName)}
          >
            <div role="heading" aria-level={1}>
              {
                typeof header === "string"
                  ? <h1>{header}</h1>
                  : header
              }
            </div>
          </div>
        }
        <div className={classNames(styles.content, contentClassName)}>
          {children}
        </div>
      </div>
    );
  });

export default PageLayout;
